import { useCallback, useMemo } from "react"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import { Button, Form, FormFeedback, Label } from "reactstrap"
import * as Yup from "yup"

import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import {
  useCreateEmployeeDesignation,
  useEditEmployeeDesignation,
} from "hooks/useEmployeeDesignation"

const EmployeeDesignationAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  editedData,
}) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateEmployeeDesignation()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditEmployeeDesignation()

  const validationSchema = Yup.object({
    title: Yup.string()
      .required("Please enter designation title")
      .max(255, "Title cannot exceed 255 characters"),
    description: Yup.string().max(
      1000,
      "Description cannot exceed 1000 characters"
    ),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const action = isEdit
          ? editMutation({ id: editedData.id, ...values })
          : createMutation(values)

        const res = await action
        toast.success(
          res?.message ??
            `Designation ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error(
          error?.response?.data?.message ?? "Failed to save designation."
        )
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: editedData?.title || "",
      description: editedData?.description || "",
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit,
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Designation"}${loading ? "..." : ""}`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = editLoading || createLoading

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Designation" : "Add Designation"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <div className="mb-3">
          <Label htmlFor="title">
            Title <span className="text-danger">*</span>
          </Label>
          <InputField
            type="text"
            name="title"
            id="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.title && !!formik.errors.title}
          />
          {formik.touched.title && formik.errors.title && (
            <FormFeedback type="invalid">{formik.errors.title}</FormFeedback>
          )}
        </div>

        <div className="mb-3">
          <Label htmlFor="description">Description</Label>
          <InputField
            type="textarea"
            name="description"
            id="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={
              formik.touched.description && !!formik.errors.description
            }
          />
          {formik.touched.description && formik.errors.description && (
            <FormFeedback type="invalid">
              {formik.errors.description}
            </FormFeedback>
          )}
        </div>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default EmployeeDesignationAddEdit
